import React, { useState } from "react";
import {
    InboxItemType,
    InviteStatus,
    ParticipantSummary,
    PlaydateInvite,
    SeenStatus,
    useUpdateInboxSeenStatusMutation,
} from "../../../gql/types";
import PortalRoot from "../../portal/PortalRoot";
import { OverlayOpacity } from "../../portal/types";
import PlaydateInviteOrganizerCanceledView from "../view/PlaydateInviteCanceledView";
import PlaydateInviteOrganizerCancellationWorkflow from "./PlaydateInviteOrganizerCancellationWorkflow";
import PlaydateInviteOrganizerCreationWorkflow from "./PlaydateInviteOrganizerCreationWorkflow";
import PlaydateInviteOrganizerPlanningWorkflow from "./PlaydateInviteOrganizerPlanningWorkflow";
import PlaydateInviteConfirmedOrganizerView from "../view/PlaydateInviteConfirmedOrganizerView";

interface Props {
    invite: PlaydateInvite;
    onBack: () => void;
    onDataChange: (success: boolean, invite?: PlaydateInvite) => void;
    baseZIndex?: number;
}

const PlaydateInviteOrganizerReviewWorkflow: React.FC<Props> = ({
    invite,
    onBack,
    onDataChange,
    baseZIndex,
}) => {
    const [showCancellationWorkflow, setShowCancellationWorkflow] = useState(false);
    const [showCreationWorkflow, setShowCreationWorkflow] = useState(false);
    const [currentStatus, setCurrentStatus] = useState(invite.status);

    const handleDeleted = (success: boolean, updatedInvite?: PlaydateInvite) => {
        setCurrentStatus(InviteStatus.Abandoned);
        setShowCancellationWorkflow(false);
        onDataChange(success, updatedInvite);
    };

    const [updateInboxSeenStatus] = useUpdateInboxSeenStatusMutation();

    React.useEffect(() => {
        if (!invite) return;

        const updateID = invite.organizerUpdate?.updateID;
        if (!updateID || invite.organizerUpdate?.seenState === SeenStatus.Read) return;

        updateInboxSeenStatus({
            variables: {
                itemType: InboxItemType.OutboundPlaydate,
                input: updateID,
            },
        }).catch((err) => {
            console.warn("Failed to mark update as seen:", err);
        });
    }, [invite, updateInboxSeenStatus]);

    if (showCancellationWorkflow) {
        return (
            <PlaydateInviteOrganizerCancellationWorkflow
                inviteID={invite.inviteID}
                onBack={() => setShowCancellationWorkflow(false)}
                onDataChange={handleDeleted}
                baseZIndex={baseZIndex}
            />
        );
    }

    if (showCreationWorkflow) {
        return (
            <PlaydateInviteOrganizerCreationWorkflow
                featureParameters={{
                    connectionIDs: invite.guests?.map((guest: ParticipantSummary) => guest.id),
                }}
                onBack={() => setShowCreationWorkflow(false)}
                baseZIndex={baseZIndex}
                onDataChange={onDataChange}
                initialConnections={invite.guests ?? []}
            />
        );
    }

    let content;
    if ((invite.status == InviteStatus.Abandoned || invite.status == InviteStatus.InPlanning) && invite.expired) {
        content = (
            <PlaydateInviteOrganizerCanceledView
                invite={invite}
                onBack={onBack}
                baseZIndex={baseZIndex}
                onCreateNewInvite={() => setShowCreationWorkflow(true)}
            />
        );
    } else {
        switch (currentStatus) {
            case InviteStatus.Abandoned:
                content = (
                    <PlaydateInviteOrganizerCanceledView
                        invite={invite}
                        onBack={onBack}
                        onCreateNewInvite={() => setShowCreationWorkflow(true)}
                        baseZIndex={baseZIndex}
                    />
                );
                break;

            case InviteStatus.InPlanning:
                content = (
                    <PlaydateInviteOrganizerPlanningWorkflow
                        invite={invite}
                        onBack={onBack}
                        onCancelInvite={() => setShowCancellationWorkflow(true)}
                        baseZIndex={baseZIndex}
                        onFinalize={(updatedInvite?: PlaydateInvite) => {
                            onDataChange(true, updatedInvite);
                        }}
                        onUpdate={(updatedInvite?: PlaydateInvite) => {
                            onDataChange(true, updatedInvite);
                        }}
                    />
                );
                break;

            default:
                content = (
                    <PlaydateInviteConfirmedOrganizerView
                        invite={invite}
                        onBack={onBack}
                        onCancel={() => setShowCancellationWorkflow(true)}
                        baseZIndex={baseZIndex}
                        onCreate={() => setShowCreationWorkflow(true)}
                    />);
        }
    }
    return (
        <PortalRoot baseZIndex={baseZIndex} overlayOpacity={OverlayOpacity.COVERED}>
            {content}
        </PortalRoot>
    );
};

export default PlaydateInviteOrganizerReviewWorkflow;
